import { useState, useEffect } from "react";
import { faLink, faLinkSlash } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin, PluginStatus } from "../../types";

interface MobiusPackage {
  name: string;
  linked: boolean;
  target?: string | null;
  version?: string | null;
}

interface MobiusStatus {
  mobius_available: boolean;
  mobius_branch?: string | null;
  mobius_commit?: string | null;
  packages: MobiusPackage[];
}

// Module-level cache so status survives drawer open/close and drives the icon.
let cached: MobiusStatus | null = null;
let fetchPromise: Promise<void> | null = null;
let listeners: Array<() => void> = [];

function notify() {
  listeners.forEach((fn) => {
    fn();
  });
}

function fetchStatus(bust = false): Promise<void> {
  if (fetchPromise && !bust) return fetchPromise;
  fetchPromise = fetch("/dev_tools/mobius/status", {
    cache: bust ? "no-store" : "default",
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((json: MobiusStatus) => {
      cached = json;
      notify();
    })
    .catch((e) => console.error("Mobius link status error:", e))
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

function useCacheSubscription() {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((fn) => fn !== listener);
    };
  }, []);
}

// Indirection so tests can stub navigation; jsdom cannot navigate for real.
export const pageNavigator = {
  go: (url: string) => {
    window.location.href = url;
  },
};

// Linking/unlinking restarts web + the dev-server, which would reload and kill
// this page mid-restart (via live_reload). Hand off to the pack-free
// reconnecting page: it has no live_reload client, so it survives the restart,
// drives the action, and returns here when web is back.
function startReconnect(action: "link" | "unlink") {
  const back =
    window.location.pathname + window.location.search + window.location.hash;
  const suffix = action === "unlink" ? "&op=unlink" : "";
  pageNavigator.go(
    `/dev_tools/mobius/reconnecting?return=${encodeURIComponent(back)}${suffix}`,
  );
}

function MobiusLinkPanel() {
  useCacheSubscription();
  const data = cached;

  if (!data) {
    return <div className="dt-mobius-link__detail">Loading...</div>;
  }

  if (!data.mobius_available) {
    return (
      <div className="dt-mobius-link__empty">
        <div className="dt-status-banner dt-status-banner--warning">
          <div className="dt-status-banner__title">Mobius is not installed locally</div>
          <div className="dt-status-banner__text">
            It cannot be linked until a local checkout exists (a sibling of
            chopin, or set <code>MOBIUS_PATH</code>).
          </div>
        </div>

        <div className="dt-mobius-link__note">
          If you want to install it, follow these instructions:
        </div>
        <pre className="dt-mobius-link__code">{`cd ~/projects  # or wherever chopin lives
git clone git@github.com:simplybusiness/mobius.git

# install and build
cd mobius
bun install
bun run build`}</pre>

        <div className="dt-mobius-link__note">
          No <code>bun</code>? Install it with{" "}
          <code>curl -fsSL https://bun.sh/install | bash</code> or{" "}
          <code>brew install oven-sh/bun/bun</code>.
        </div>
        <div className="dt-mobius-link__note">
          <a
            className="dt-mobius-link__docs"
            href="https://github.com/simplybusiness/mobius#readme"
            target="_blank"
            rel="noreferrer"
          >
            Mobius setup &amp; HMR docs
          </a>
        </div>
      </div>
    );
  }

  const unlinkedCount = data.packages.filter((p) => !p.linked).length;
  // Once everything is linked the button toggles to Unlink (restores the npm
  // packages via `bin/link-mobius off`), so it is never a dead "all linked" end
  // state.
  const allLinked = data.packages.length > 0 && unlinkedCount === 0;

  return (
    <>
      {data.mobius_branch && (
        <div className="dt-mobius-link__branch">
          mobius <code>{data.mobius_branch}</code>
          {data.mobius_commit ? ` @ ${data.mobius_commit}` : ""}
        </div>
      )}

      <button
        className="dt-btn dt-btn--primary dt-mobius-link__link-all"
        type="button"
        disabled={data.packages.length === 0}
        onClick={() => startReconnect(allLinked ? "unlink" : "link")}
      >
        {allLinked ? "Unlink packages" : "Link packages locally"}
      </button>

      <div className="dt-section-label">Packages</div>
      {data.packages.map((p) => (
        <div key={p.name} className="dt-mobius-link__row">
          <span
            className={`dt-mobius-link__badge dt-mobius-link__badge--${
              p.linked ? "linked" : "npm"
            }`}
          >
            {p.linked ? "linked" : "npm"}
          </span>
          <span className="dt-mobius-link__name">{p.name}</span>
          <span className="dt-mobius-link__detail">
            {p.linked ? "local mobius" : p.version || ""}
          </span>
        </div>
      ))}

      <div className="dt-mobius-link__note">
        Opens a reconnecting screen that links mobius, restarts the bundler and
        web (~10s), then returns here. Renders CSR-only while linked; component
        edits are live; theme/variable edits need a mobius theme build (chopin
        consumes published <code>dist</code>). Unlink restores the published npm
        packages (runs <code>yarn install</code>, so it is slower).
      </div>
    </>
  );
}

export function mobiusLinkPlugin(): DevToolsPlugin {
  if (typeof document !== "undefined") fetchStatus();

  return {
    id: "mobius-link",
    name: "Mobius Link",
    // Dynamic: faLink once anything is linked, faLinkSlash otherwise (the
    // startup default, and while loading / no mobius checkout). Read at render
    // time by PluginIconButton, which subscribes via useStatus.
    icon: () => (cached?.packages.some((p) => p.linked) ? faLink : faLinkSlash),
    render: () => <MobiusLinkPanel />,
    useStatus: (): PluginStatus => {
      useCacheSubscription();
      if (!cached) return { state: "loading" };
      const linked = cached.packages.filter((p) => p.linked).length;
      if (linked === 0) return { state: "inactive" };
      return { state: "active", badge: linked, summary: `${linked} linked` };
    },
  };
}
